import React, { useState } from "react";
import { Container, Card, Spinner, Button } from "react-bootstrap";
import {
  Bell,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Info,
  CheckCheck,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import * as notificationService from "../../services/notificationServices.js";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Clock } from "react-bootstrap-icons";

const NotificationsPage = () => {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("all"); // 'all' or 'unread'

  // ==========================================
  // 🔴 LOGIC SECTION (UNTOUCHED & SECURED) 🔴
  // ==========================================
  const {
    data: notifications = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["notificationsList"],
    queryFn: async () => {
      const data = await notificationService.getNotifications();
      return data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    staleTime: 55000,
  });

  const markReadMutation = useMutation({
    mutationFn: notificationService.markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(["notificationsList"]);
      queryClient.invalidateQueries(["unreadNotifications"]);
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: notificationService.markAllNotificationsAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries(["notificationsList"]);
      queryClient.invalidateQueries(["unreadNotifications"]);
    },
  });

  const clearAllMutation = useMutation({
    mutationFn: notificationService.clearNotifications,
    onSuccess: () => {
      queryClient.setQueryData(["notificationsList"], []);
      queryClient.invalidateQueries(["unreadNotifications"]);
    },
  });

  const handleMarkAsRead = (id, isRead) => {
    if (!isRead) markReadMutation.mutate(id);
  };

  const filteredNotifications = notifications.filter((n) =>
    filter === "all" ? true : !n.isRead,
  );

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  // تم تظبيط الألوان لتناسب الـ Dark Theme
  const getIcon = (type) => {
    const size = 22;
    switch (type) {
      case "success":
        return (
          <div className="p-2 rounded-circle bg-success bg-opacity-25 border border-success border-opacity-50">
            <CheckCircle className="text-success" size={size} />
          </div>
        );
      case "error":
        return (
          <div className="p-2 rounded-circle bg-danger bg-opacity-25 border border-danger border-opacity-50">
            <XCircle className="text-danger" size={size} />
          </div>
        );
      case "warning":
        return (
          <div className="p-2 rounded-circle bg-warning bg-opacity-25 border border-warning border-opacity-50">
            <AlertTriangle className="text-warning" size={size} />
          </div>
        );
      default:
        return (
          <div className="p-2 rounded-circle bg-info bg-opacity-25 border border-info border-opacity-50">
            <Info className="text-info" size={size} />
          </div>
        );
    }
  };
  // ==========================================
  // 🟢 END OF LOGIC SECTION 🟢
  // ==========================================

  // 4️⃣ Loading State (Dark Theme)
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ backgroundColor: "#0f172a" }}
      >
        <div className="text-center d-flex flex-column align-items-center gap-3">
          <Spinner
            animation="border"
            variant="info"
            style={{ width: "3.5rem", height: "3.5rem" }}
          />
          <div className="d-flex flex-column">
            <span
              className="text-white fw-bold"
              dir="rtl"
              style={{ fontSize: "1.1rem" }}
            >
              جاري تحميل الإشعارات...
            </span>
            <span className="text-secondary" dir="ltr">
              Loading your updates...
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: "#0f172a",
        minHeight: "100vh",
        paddingTop: "80px",
        paddingBottom: "60px",
        marginTop: "20px",
      }}
    >
      <Container style={{ maxWidth: "850px" }}>
        {/* Header Section */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-end mb-4 gap-4">
          <div className="d-flex flex-column">
            <div className="d-flex align-items-center gap-3 mb-2">
              <div className="bg-primary bg-opacity-25 p-2 rounded-circle border border-primary border-opacity-25">
                <Bell size={28} className="text-info" />
              </div>
              <div>
                <h2
                  className="fw-bold text-white mb-0 d-flex align-items-center gap-2"
                  dir="rtl"
                >
                  الإشعارات
                  {unreadCount > 0 && (
                    <span
                      className="badge bg-danger rounded-pill px-2 py-1 shadow-sm"
                      style={{
                        fontSize: "0.85rem",
                        transform: "translateY(-2px)",
                      }}
                    >
                      {unreadCount} جديد
                    </span>
                  )}
                </h2>
                <span
                  className="text-info fw-semibold"
                  dir="ltr"
                  style={{ fontSize: "0.9rem", letterSpacing: "0.5px" }}
                >
                  Notifications
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2 mt-2 px-1">
              <span
                className="text-light"
                dir="rtl"
                style={{ fontSize: "0.95rem" }}
              >
                تابع أحدث نشاطاتك
              </span>
              <span className="text-secondary small d-none d-sm-block">|</span>
              <span className="text-secondary small" dir="ltr">
                Stay updated with your latest activities
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-light"
              className="d-flex align-items-center gap-2 rounded-pill px-3 py-2 border-opacity-25 action-btn"
              onClick={() => markAllReadMutation.mutate()}
              disabled={unreadCount === 0 || markAllReadMutation.isLoading}
              style={{ background: "rgba(255,255,255,0.05)" }}
            >
              <CheckCheck size={16} />
              <div className="d-flex flex-column align-items-start lh-1">
                <span
                  className="fw-bold"
                  dir="rtl"
                  style={{ fontSize: "0.8rem" }}
                >
                  تحديد كمقروء
                </span>
                <span style={{ fontSize: "0.65rem", opacity: 0.8 }} dir="ltr">
                  Mark all read
                </span>
              </div>
            </Button>
            <Button
              variant="outline-danger"
              className="d-flex align-items-center gap-2 rounded-pill px-3 py-2 border-opacity-25 hover-danger action-btn"
              onClick={() => clearAllMutation.mutate()}
              disabled={
                notifications.length === 0 || clearAllMutation.isLoading
              }
              style={{ background: "rgba(220, 53, 69, 0.1)" }}
            >
              <Trash2 size={16} />
              <div className="d-flex flex-column align-items-start lh-1">
                <span
                  className="fw-bold"
                  dir="rtl"
                  style={{ fontSize: "0.8rem" }}
                >
                  مسح الكل
                </span>
                <span style={{ fontSize: "0.65rem", opacity: 0.8 }} dir="ltr">
                  Clear all
                </span>
              </div>
            </Button>
          </div>
        </div>

        {/* Filters Tabs */}
        <div className="bg-dark bg-opacity-50 rounded-pill p-1 d-inline-flex mb-4 shadow-sm border border-white border-opacity-10">
          <Button
            variant={filter === "all" ? "primary" : "transparent"}
            className={`rounded-pill px-4 py-2 d-flex flex-column align-items-center lh-1 border-0 ${filter === "all" ? "shadow-sm" : "text-secondary hover-text-white"}`}
            onClick={() => setFilter("all")}
            style={{ transition: "all 0.3s ease", minWidth: "100px" }}
          >
            <span
              className="fw-bold mb-1"
              dir="rtl"
              style={{ fontSize: "0.9rem" }}
            >
              الكل
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                opacity: filter === "all" ? 0.9 : 0.7,
              }}
              dir="ltr"
            >
              All
            </span>
          </Button>
          <Button
            variant={filter === "unread" ? "primary" : "transparent"}
            className={`rounded-pill px-4 py-2 d-flex flex-column align-items-center lh-1 border-0 ${filter === "unread" ? "shadow-sm" : "text-secondary hover-text-white"}`}
            onClick={() => setFilter("unread")}
            style={{ transition: "all 0.3s ease", minWidth: "100px" }}
          >
            <span
              className="fw-bold mb-1"
              dir="rtl"
              style={{ fontSize: "0.9rem" }}
            >
              غير مقروء
            </span>
            <span
              style={{
                fontSize: "0.7rem",
                opacity: filter === "unread" ? 0.9 : 0.7,
              }}
              dir="ltr"
            >
              Unread
            </span>
          </Button>
        </div>

        {isFetching && !isLoading && (
          <div className="d-flex align-items-center gap-2 mb-3 ms-2 text-info animate-pulse">
            <Spinner animation="grow" size="sm" />
            <span className="small fw-semibold">
              جاري التحديث... | Syncing...
            </span>
          </div>
        )}

        {/* Notifications List */}
        <div className="d-flex flex-column gap-3">
          {filteredNotifications.length === 0 ? (
            <div
              className="text-center py-5 rounded-4 border border-white border-opacity-10"
              style={{ background: "rgba(255,255,255,0.02)" }}
            >
              <div className="bg-white bg-opacity-10 p-4 rounded-circle d-inline-block mb-3 border border-white border-opacity-10">
                <Bell size={45} className="text-secondary opacity-75" />
              </div>
              <h5 className="text-white fw-bold mb-2" dir="rtl">
                لا توجد إشعارات
              </h5>
              <p className="text-secondary mb-1" dir="ltr">
                No notifications found
              </p>
              <p className="text-primary small" dir="rtl">
                سنعلمك فور وصول أي جديد!
              </p>
            </div>
          ) : (
            filteredNotifications.map((notif) => (
              <Card
                key={notif._id}
                onClick={() => handleMarkAsRead(notif._id, notif.isRead)}
                className={`border-0 rounded-4 shadow-sm notification-card ${!notif.isRead ? "unread" : ""}`}
                style={{
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.05)",
                }}
              >
                <Card.Body className="p-3 p-md-4 d-flex gap-3 align-items-start">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getIcon(notif.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-grow-1 min-w-0">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start mb-2 gap-2">
                      <h6
                        className={`mb-0 text-break ${!notif.isRead ? "fw-bold text-white" : "fw-semibold text-light opacity-75"}`}
                        style={{ fontSize: "1.05rem", lineHeight: "1.4" }}
                        dir="auto" // بيضبط الاتجاه حسب لغة النص الجاي من الداتابيز
                      >
                        {notif.title}
                      </h6>
                      <div className="d-flex align-items-center gap-2 flex-shrink-0 bg-dark bg-opacity-50 px-2 py-1 rounded-3 border border-white border-opacity-10">
                        <Clock size={12} className="text-secondary" />
                        <small
                          className="text-secondary fw-medium"
                          style={{
                            fontSize: "0.75rem",
                            letterSpacing: "0.5px",
                          }}
                          dir="ltr"
                        >
                          {new Date(notif.createdAt).toLocaleDateString()} •{" "}
                          {new Date(notif.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </small>
                      </div>
                    </div>

                    <p
                      className={`mb-3 text-break ${!notif.isRead ? "text-light" : "text-secondary"}`}
                      style={{ fontSize: "0.95rem", lineHeight: "1.6" }}
                      dir="auto"
                    >
                      {notif.message}
                    </p>

                    {notif.link && (
                      <Link
                        to={notif.link}
                        onClick={(e) => e.stopPropagation()}
                        className="btn btn-sm px-4 py-2 rounded-pill d-inline-flex align-items-center gap-2 hover-link fw-bold border-0"
                        style={{
                          background:
                            "linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)",
                          color: "white",
                          fontSize: "0.85rem",
                        }}
                      >
                        <span dir="rtl">عرض التفاصيل</span>
                        <span className="opacity-50">|</span>
                        <span dir="ltr">
                          View Details <span className="ms-1">→</span>
                        </span>
                      </Link>
                    )}
                  </div>

                  {/* Unread Dot Indicator */}
                  {!notif.isRead && (
                    <div className="position-absolute top-50 end-0 translate-middle-y me-3 me-md-4">
                      <span
                        className="p-1 rounded-circle bg-info d-block shadow-lg indicator-dot"
                        style={{
                          boxShadow: "0 0 10px rgba(14, 165, 233, 0.8)",
                        }}
                      ></span>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      </Container>

      {/* Custom Styles */}
      <style>{`
        .notification-card {
            position: relative;
            overflow: hidden;
            backdrop-filter: blur(10px);
        }
        .notification-card.unread {
            background: rgba(14, 165, 233, 0.05) !important;
            border-left: 4px solid #0ea5e9 !important;
        }
        .notification-card:hover {
            transform: translateY(-3px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.2) !important;
            border-color: rgba(255,255,255,0.15) !important;
        }
        .action-btn {
            transition: all 0.3s ease;
        }
        .action-btn:hover {
            transform: translateY(-2px);
        }
        .hover-danger:hover {
            background-color: #dc3545 !important;
            color: white !important;
            border-color: #dc3545 !important;
        }
        .hover-text-white:hover {
            color: white !important;
            background: rgba(255,255,255,0.05);
        }
        .hover-link:hover {
            filter: brightness(1.1);
            transform: scale(1.02);
        }
        .indicator-dot {
            width: 12px;
            height: 12px;
        }
        @keyframes pulse {
            0% { opacity: 0.5; transform: scale(0.95); }
            50% { opacity: 1; transform: scale(1.05); }
            100% { opacity: 0.5; transform: scale(0.95); }
        }
        .animate-pulse {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default NotificationsPage;

// import React, { useState } from "react";
// import {
//   Container,
//   Card,
//   Spinner,
//   Button,
//   // Badge,
//   // Nav
// } from "react-bootstrap";
// import {
//   Bell,
//   CheckCircle,
//   XCircle,
//   AlertTriangle,
//   Info,
//   CheckCheck,
//   Trash2,
//   // Filter
// } from "lucide-react";
// import { Link } from "react-router-dom";
// import * as notificationService from "../../services/notificationServices.js";
// import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

// const NotificationsPage = () => {
//   const queryClient = useQueryClient();
//   const [filter, setFilter] = useState("all"); // 'all' or 'unread'

//   // 1️⃣ Fetch Notifications using React Query (Auto Syncs with Bell!)
//   const {
//     data: notifications = [],
//     isLoading,
//     isFetching,
//   } = useQuery({
//     queryKey: ["notificationsList"], // Key فريد للقائمة
//     queryFn: async () => {
//       const data = await notificationService.getNotifications();
//       return data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
//     },
//     refetchInterval: 60000, // تحديث كل دقيقة (زي الجرس بالظبط)
//     refetchOnWindowFocus: true, // تحديث أول ما يفتح الصفحة
//     staleTime: 55000, // الداتا تفضل طازة 5 ثواني
//   });

//   // 2️⃣ Mutations for Actions (Optimistic Updates for speed)
//   const markReadMutation = useMutation({
//     mutationFn: notificationService.markNotificationAsRead,
//     onSuccess: () => {
//       queryClient.invalidateQueries(["notificationsList"]); // تحديث القائمة
//       queryClient.invalidateQueries(["unreadNotifications"]); // تحديث الجرس
//     },
//   });

//   const markAllReadMutation = useMutation({
//     mutationFn: notificationService.markAllNotificationsAsRead,
//     onSuccess: () => {
//       queryClient.invalidateQueries(["notificationsList"]);
//       queryClient.invalidateQueries(["unreadNotifications"]);
//     },
//   });

//   const clearAllMutation = useMutation({
//     mutationFn: notificationService.clearNotifications,
//     onSuccess: () => {
//       queryClient.setQueryData(["notificationsList"], []); // فضي القائمة فوراً
//       queryClient.invalidateQueries(["unreadNotifications"]);
//     },
//   });

//   // 3️⃣ Helper Functions
//   const handleMarkAsRead = (id, isRead) => {
//     if (!isRead) markReadMutation.mutate(id);
//   };

//   // Filter Logic
//   const filteredNotifications = notifications.filter((n) =>
//     filter === "all" ? true : !n.isRead,
//   );

//   const unreadCount = notifications.filter((n) => !n.isRead).length;

//   const getIcon = (type) => {
//     const size = 20;
//     switch (type) {
//       case "success":
//         return (
//           <div className="p-2 rounded-circle bg-success-subtle">
//             <CheckCircle className="text-success" size={size} />
//           </div>
//         );
//       case "error":
//         return (
//           <div className="p-2 rounded-circle bg-danger-subtle">
//             <XCircle className="text-danger" size={size} />
//           </div>
//         );
//       case "warning":
//         return (
//           <div className="p-2 rounded-circle bg-warning-subtle">
//             <AlertTriangle className="text-warning" size={size} />
//           </div>
//         );
//       default:
//         return (
//           <div className="p-2 rounded-circle bg-primary-subtle">
//             <Info className="text-primary" size={size} />
//           </div>
//         );
//     }
//   };

//   // 4️⃣ Loading State
//   if (isLoading) {
//     return (
//       <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
//         <div className="text-center">
//           <Spinner
//             animation="border"
//             variant="primary"
//             style={{ width: "3rem", height: "3rem" }}
//           />
//           <p className="mt-2 text-muted fw-medium">Loading your updates...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
//       <Container className="py-5 mt-5" style={{ maxWidth: "800px" }}>
//         {/* Header Section */}
//         <div className="d-flex flex-column flex-md-row justify-content-between align-items-end mb-4 gap-3">
//           <div>
//             <h2 className="fw-bold text-dark d-flex align-items-center gap-2 mb-1">
//               Notifications
//               {unreadCount > 0 && (
//                 <p
//                   bg="danger"
//                   pill
//                   style={{ fontSize: "0.5em", verticalAlign: "top" }}
//                 >
//                   {unreadCount} New
//                 </p>
//               )}
//             </h2>
//             <p className="text-muted mb-0">
//               Stay updated with your latest activities
//             </p>
//           </div>

//           <div className="d-flex gap-2">
//             <Button
//               variant="white"
//               className="border shadow-sm text-muted d-flex align-items-center gap-2"
//               onClick={() => markAllReadMutation.mutate()}
//               disabled={unreadCount === 0 || markAllReadMutation.isLoading}
//             >
//               <CheckCheck size={16} />{" "}
//               <span className="d-none d-sm-inline">Mark all read</span>
//             </Button>
//             <Button
//               variant="white"
//               className="border shadow-sm text-danger d-flex align-items-center gap-2 hover-danger"
//               onClick={() => clearAllMutation.mutate()}
//               disabled={
//                 notifications.length === 0 || clearAllMutation.isLoading
//               }
//             >
//               <Trash2 size={16} />{" "}
//               <span className="d-none d-sm-inline">Clear all</span>
//             </Button>
//           </div>
//         </div>

//         {/* Filters Tabs */}
//         <div className="bg-white rounded-3 p-1 d-inline-flex mb-4 shadow-sm border">
//           <Button
//             variant={filter === "all" ? "primary" : "light"}
//             className={`rounded-2 px-4 py-1 fw-medium ${filter === "all" ? "" : "text-muted bg-white border-0"}`}
//             onClick={() => setFilter("all")}
//           >
//             All
//           </Button>
//           <Button
//             variant={filter === "unread" ? "primary" : "light"}
//             className={`rounded-2 px-4 py-1 fw-medium ${filter === "unread" ? "" : "text-muted bg-white border-0"}`}
//             onClick={() => setFilter("unread")}
//           >
//             Unread
//           </Button>
//         </div>

//         {isFetching && !isLoading && (
//           <small className="text-primary ms-3 animate-pulse">Syncing...</small>
//         )}

//         {/* Notifications List */}
//         <div className="d-flex flex-column gap-3">
//           {filteredNotifications.length === 0 ? (
//             <div className="text-center py-5">
//               <div className="bg-white p-4 rounded-circle d-inline-block shadow-sm mb-3">
//                 <Bell size={40} className="text-muted opacity-50" />
//               </div>
//               <h5 className="text-muted">No notifications found</h5>
//               <p className="text-muted small">
//                 We'll let you know when something arrives!
//               </p>
//             </div>
//           ) : (
//             filteredNotifications.map((notif) => (
//               <Card
//                 key={notif._id}
//                 onClick={() => handleMarkAsRead(notif._id, notif.isRead)}
//                 className={`border-0 rounded-3 shadow-sm notification-card ${!notif.isRead ? "unread" : ""}`}
//                 style={{ cursor: "pointer", transition: "all 0.2s ease" }}
//               >
//                 <Card.Body className="p-3 d-flex gap-3 align-items-start">
//                   {/* Icon */}
//                   <div className="flex-shrink-0 mt-1">
//                     {getIcon(notif.type)}
//                   </div>

//                   {/* Content */}
//                   <div className="flex-grow-1 min-w-0">
//                     <div className="d-flex justify-content-between align-items-start mb-1">
//                       <h6
//                         className={`mb-0 text-truncate ${!notif.isRead ? "fw-bold text-dark" : "fw-semibold text-secondary"}`}
//                       >
//                         {notif.title}
//                       </h6>
//                       <small
//                         className="text-muted ms-2 flex-shrink-0"
//                         style={{ fontSize: "0.75rem" }}
//                       >
//                         {new Date(notif.createdAt).toLocaleDateString()} •{" "}
//                         {new Date(notif.createdAt).toLocaleTimeString([], {
//                           hour: "2-digit",
//                           minute: "2-digit",
//                         })}
//                       </small>
//                     </div>

//                     <p
//                       className={`mb-2 text-break ${!notif.isRead ? "text-dark" : "text-muted"}`}
//                       style={{ fontSize: "0.9rem", lineHeight: "1.5" }}
//                     >
//                       {notif.message}
//                     </p>

//                     {notif.link && (
//                       <Link
//                         to={notif.link}
//                         onClick={(e) => e.stopPropagation()}
//                         className="btn btn-sm btn-light text-primary fw-medium px-3 rounded-pill d-inline-flex align-items-center gap-1 hover-link"
//                         style={{ fontSize: "0.8rem" }}
//                       >
//                         View Details <span>→</span>
//                       </Link>
//                     )}
//                   </div>

//                   {/* Unread Dot Indicator */}
//                   {!notif.isRead && (
//                     <div className="position-absolute top-50 end-0 translate-middle-y me-3">
//                       <span className="p-1 rounded-circle bg-primary d-block shadow-sm indicator-dot"></span>
//                     </div>
//                   )}
//                 </Card.Body>
//               </Card>
//             ))
//           )}
//         </div>
//       </Container>

//       {/* Custom Styles */}
//       <style>{`
//         .notification-card {
//             position: relative;
//             overflow: hidden;
//             background: white;
//         }
//         .notification-card.unread {
//             background: #ffffff;
//             border-left: 4px solid #0d6efd !important;
//             background-color: #f8faff; /* لون أزرق فاتح جداً جداً للخلفية */
//         }
//         .notification-card:hover {
//             transform: translateY(-2px);
//             box-shadow: 0 .5rem 1rem rgba(0,0,0,.08) !important;
//         }
//         .hover-danger:hover {
//             background-color: #dc3545 !important;
//             color: white !important;
//             border-color: #dc3545 !important;
//         }
//         .hover-link:hover {
//             background-color: #e7f1ff;
//             color: #0056b3;
//         }
//         .indicator-dot {
//             width: 10px;
//             height: 10px;
//         }
//         @keyframes pulse {
//             0% { opacity: 0.6; }
//             50% { opacity: 1; }
//             100% { opacity: 0.6; }
//         }
//         .animate-pulse {
//             animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
//         }
//       `}</style>
//     </div>
//   );
// };

// export default NotificationsPage;
